/** Device / report: DD-MM-YYYY (day-month-year), optional time. */
const DD_MM_YYYY_TIME =
  /^(\d{1,2})-(\d{1,2})-(\d{4})\s*,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DD_MM_YYYY_TIME_NO_SPACE =
  /^(\d{1,2})-(\d{1,2})-(\d{4})(\d{2}):(\d{2})(?::(\d{2}))?$/;
const DD_MM_YYYY_DATE_ONLY = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
const DEVICE_SPACE_COMMA =
  /^(\d{1,2})\s+(\d{1,2})\s+(\d{4})\s*,\s*(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

function pad2(n) {
  return String(Number(n)).padStart(2, '0');
}

function buildLocalDate(dd, mm, yyyy, hh = 0, min = 0, sec = 0) {
  const d = new Date(yyyy, mm - 1, dd, hh, min, sec, 0);
  return isNaN(d.getTime()) ? null : d;
}

/** Format parts as DD-MM-YYYY HH:mm:ss */
export function formatDdMmYyyyHms(dd, mm, yyyy, hh, min, sec) {
  return `${pad2(dd)}-${pad2(mm)}-${yyyy} ${pad2(hh)}:${pad2(min)}:${pad2(sec ?? 0)}`;
}

/**
 * Parse DD-MM-YYYY strings to Date (never uses Date.parse on dashed dates).
 * Accepts: 01-06-2026, 01-06-2026 11:50:14, 01-06-202611:50:14, device "1 6 2026,11:50:14".
 */
export function parseDdMmYyyyDateTime(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;

  let m = s.match(DD_MM_YYYY_TIME);
  if (m) {
    return buildLocalDate(
      parseInt(m[1], 10),
      parseInt(m[2], 10),
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      m[6] != null ? parseInt(m[6], 10) : 0
    );
  }

  m = s.match(DD_MM_YYYY_TIME_NO_SPACE);
  if (m) {
    return buildLocalDate(
      parseInt(m[1], 10),
      parseInt(m[2], 10),
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      m[6] != null ? parseInt(m[6], 10) : 0
    );
  }

  m = s.match(DD_MM_YYYY_DATE_ONLY);
  if (m) {
    return buildLocalDate(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
  }

  m = s.match(DEVICE_SPACE_COMMA);
  if (m) {
    return buildLocalDate(
      parseInt(m[1], 10),
      parseInt(m[2], 10),
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      m[6] != null ? parseInt(m[6], 10) : 0
    );
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(s) || s.includes('T')) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/**
 * Display time as DD-MM-YYYY HH:mm:ss. Preserves DD-MM-YYYY; never swaps day/month.
 */
export function formatDisplayDateTime(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';

  let m = trimmed.match(DD_MM_YYYY_TIME);
  if (m) {
    return formatDdMmYyyyHms(m[1], m[2], m[3], m[4], m[5], m[6] != null ? m[6] : 0);
  }

  m = trimmed.match(DD_MM_YYYY_TIME_NO_SPACE);
  if (m) {
    return formatDdMmYyyyHms(m[1], m[2], m[3], m[4], m[5], m[6] != null ? m[6] : 0);
  }

  m = trimmed.match(DD_MM_YYYY_DATE_ONLY);
  if (m) {
    return `${pad2(m[1])}-${pad2(m[2])}-${m[3]} 00:00:00`;
  }

  m = trimmed.match(DEVICE_SPACE_COMMA);
  if (m) {
    return formatDdMmYyyyHms(m[1], m[2], m[3], m[4], m[5], m[6] != null ? m[6] : 0);
  }

  const parsed = parseDdMmYyyyDateTime(trimmed);
  if (parsed) {
    return formatDdMmYyyyHms(
      parsed.getDate(),
      parsed.getMonth() + 1,
      parsed.getFullYear(),
      parsed.getHours(),
      parsed.getMinutes(),
      parsed.getSeconds()
    );
  }

  return trimmed;
}

/** Report row timestamp (ms): API `time` as DD-MM-YYYY first, then recordedAt. */
export function getRowTimeMs(row) {
  if (row == null) return NaN;
  const rawTime = row.time != null && String(row.time).trim() !== '' ? String(row.time).trim() : '';
  if (rawTime) {
    const dt = parseDdMmYyyyDateTime(rawTime);
    if (dt) return dt.getTime();
  }
  if (row.recordedAt != null) {
    const ra = new Date(row.recordedAt);
    if (!isNaN(ra.getTime())) return ra.getTime();
  }
  return NaN;
}

/** Report row as Date for filter range checks. */
export function getRowDateTime(row) {
  const ms = getRowTimeMs(row);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

/** Report table row: prefer API `time`, else recordedAt. */
export function formatRecordTimeFromRow(row) {
  if (row === null || row === undefined) return '';
  const raw =
    row.time != null && String(row.time).trim() !== ''
      ? String(row.time).trim()
      : row.recordedAt ?? row.time;
  return formatDisplayDateTime(raw);
}
